// The chevron on a CTA, animated.
//
// Idle: a small repeating nudge to the right — the affordance that says "this
// goes somewhere". Hovered: it commits and slides further out, so the pointer
// lands on a control that has already reacted.

import React, {useEffect} from 'react';
import {TextStyle} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import CCText from '@/Atoms/CCText';

interface NudgeArrowProps {
  hovered?: boolean;
  glyph?: string;
  color?: string;
  fontSize?: number;
  /** Stagger the idle loop when several arrows share a screen. */
  delay?: number;
  style?: TextStyle;
}

export default function NudgeArrow({
  hovered,
  glyph = '›',
  color,
  fontSize = 14,
  delay = 0,
  style,
}: NudgeArrowProps) {
  const idle = useSharedValue(0);
  const push = useSharedValue(0);

  useEffect(() => {
    idle.value = withRepeat(
      withSequence(
        // Long pause between nudges: a constantly-moving arrow reads as noise.
        withTiming(0, {duration: 1400 + delay}),
        withTiming(1, {duration: 260, easing: Easing.out(Easing.quad)}),
        withTiming(0, {duration: 320, easing: Easing.inOut(Easing.quad)}),
      ),
      -1,
      false,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    push.value = withTiming(hovered ? 1 : 0, {
      duration: 200,
      easing: Easing.out(Easing.cubic),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered]);

  const animStyle = useAnimatedStyle(() => ({
    transform: [{translateX: idle.value * 3 + push.value * 4}],
  }));

  return (
    <Animated.View style={animStyle}>
      <CCText fontWeight="bold" fontSize={fontSize} color={color} style={style}>
        {glyph}
      </CCText>
    </Animated.View>
  );
}
